import { Localized } from "@fluent/react";
import {
  Dialog, DialogActions, DialogContent, DialogTitle, TextField, MenuItem,
  Paper,
  Box,
  Typography,
  Divider,
  InputAdornment,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useAppDispatch } from "../../../../app/hooks";
import { SaveButton } from "../../../../app/components/SaveButton";
import { CancelButton } from "../../../../app/components/CancelButton";
import { CompanyProfile, CompanyCountryMarket, CompanyMarket, CompanyQuickTag, CompanySocialLink, CompanyCertificates, updateCompanyProfileDescription, updateCountryMarkets, updateMarkets, updateQuickTags, updateSocialLinks, updateCertificates, updateMarketplaceConsents } from "../companyProfileApi";
import CountryMultiSelect from "../controls/CountryMultiSelect";
import CountrySelect from "../controls/CountrySelect";
import MarketRegionMultiSelect from "../controls/MarketRegionMultiSelect";
import { countriesAtlas } from "../countriesApi";
import EnumMultiSelect from "../../../../app/components/EnumMultiSelect";
import { QUICK_TAG_OPTIONS } from "../enums/QuickTagEnum";
import { QuickTagEnum } from "../enums/QuickTagEnum";
import { CertificatesMultiSelect } from "../controls/CertificatesMultiSelect";
import { CompanyProfileCompanySizeMenu } from "../controls/CompanyProfileCompanySizeMenu";
import { FieldKey } from "../controls/KeyValueRow";
import { SocialPlatformEnum } from "../enums/socialPlatformEnum";
import { MarketplaceConsentsEditor } from "../controls/MarketplaceConsentsEditor";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

export interface EditCompanyProcessFieldProps {
  companyId: number;
  companyProfile?: CompanyProfile;
  field: FieldKey;
  value: ReactNode;
  onSave: (project: CompanyProfile) => void;
  onCancel: () => void;
}

type FieldType = "string" | "number" | "multi-select" | "countries" | "regions"
| "company-size" | "certificates" | "marketplace" | "year";

type FieldMeta = {
  type: FieldType;
  label?: React.ReactNode;
  options?: Array<{ id: number | string; name: string }>;
  placeholder?: string;
};

const FIELD_META: Record<string, FieldMeta> = {
  legalName: { type: "string", label: <Localized id="companies-name">_Company Name</Localized> },
  shortName: { type: "string", label: <Localized id="companies-short-name">_Short name</Localized> },
  about: { type: "string", label: <Localized id="about">_About</Localized> },
  yearFounded: { type: "year", label: <Localized id="companies-founded">_Year founded</Localized> },
  companySizeTierId: { type: "company-size", label: <Localized id="companies-size">_Company size</Localized> },

  websiteUrl: { type: "string", label: <Localized id="website">_Website</Localized> },
  primaryEmail: { type: "string", label: <Localized id="companies-primary-email">_Primary email</Localized> },
  salesEmail: { type: "string", label: <Localized id="companies-sales-email">_Sales email</Localized> },
  supportEmail: { type: "string", label: <Localized id="companies-support-email">_Support email</Localized> },
  phone: { type: "string", label: <Localized id="companies-phone">_Phone</Localized> },
  whatsApp: { type: "string", label: "WhatsApp" },

  hQ_Street: { type: "string", label: <Localized id="companies-street">_Street</Localized> },
  hQ_BuildingNumber: { type: "string", label: <Localized id="companies-building-number">_Building number</Localized> },
  hQ_UnitNumber: { type: "string", label: <Localized id="companies-unit-number">_Unit number</Localized> },
  hQ_AddressAdditional: { type: "string", label: <Localized id="companies-address-additional">_Address additional</Localized> },
  hQ_City: { type: "string", label: <Localized id="companies-address-city">_City</Localized> },
  hQ_Region: { type: "string", label: <Localized id="companies-region">_Region</Localized> },
  hQ_RegionCode: { type: "string", label: <Localized id="companies-region-code">_Region code</Localized> },
  hQ_PostalCode: { type: "string", label: <Localized id="companies-postal-code">_Postal code</Localized> },
  hQ_CountryCode: { type: "string", label: <Localized id="companies-country-code">_Country code</Localized> },
  hQ_CountryName: { type: "string", label: <Localized id="companies-country-name">_Country</Localized> },
  
  quickTags: {
    type: "multi-select",
    label: <Localized id="quick-tags">_Quick tags</Localized>,
    options: QUICK_TAG_OPTIONS.map(o => ({ id: o.id, name: o.label })),
    placeholder: "Select quick tags",
  },

  countryMarkets: { type: "countries", label: <Localized id="markets-countries">_Markets (countries)</Localized> },
  regionMarkets: { type: "regions", label: <Localized id="markets-regions">_Markets (regions)</Localized> },
  certificates: { type: "certificates", label: <Localized id="companies-certificates">_Certificates</Localized> },
  socialLink: { type: "string", label: <Localized id="companies-social-link">_Social Link URL</Localized> },
  marketplace: { type: "marketplace", label: <Localized id="companies-marketplace">_Marketplace</Localized> },
};

export const EditCompanyProfileField = (props: EditCompanyProcessFieldProps) => {
  const dispatch = useAppDispatch();
  const { companyId, field, value } = props;

  const getInitialValue = () => {
    if (field === "quickTags" && Array.isArray(value)) {
      return value.map((v: any) => v.quickTagId ?? v.id ?? v);
    }

    if (field === "countryMarkets" || field === "regionMarkets" || field === "certificates" || field === "marketplace") {
      return Array.isArray(value) ? value : [];
    }

    if (field === "socialLink") {
      const v = value as any;
      return v?.url ?? "";
    }

    // Normalize country code/name from database (handle inconsistencies)
    if (field === "hQ_CountryCode" || field === "hQ_CountryName") {
      const strValue = String(value ?? "").trim();
      if (strValue && field === "hQ_CountryCode" && strValue.length > 3) {
        // Value looks like a country name, find the code
        const found = countriesAtlas.find((c: any) => 
          (c.label ?? "").toLowerCase() === strValue.toLowerCase()
        );
        return found ? found.code : strValue;
      }
      if (strValue && field === "hQ_CountryName" && strValue.length <= 3) {
        // Value looks like a country code, find the name
        const found = countriesAtlas.find((c: any) => 
          (c.code ?? "").toLowerCase() === strValue.toLowerCase()
        );
        return found ? found.label : strValue;
      }
    }

    return value ?? "";
  };

  const [newValue, setNewValue] = useState<any>(getInitialValue());
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string>("");
  const [marketActive, setMarketActive] = useState<boolean>(
    () => (props.companyProfile as any)?.isMarketPlaceActive ?? false
  );
  const [restrictVisibilityToBuyers, setRestrictVisibilityToBuyers] = useState<boolean>(
    () => (props.companyProfile as any)?.restrictVisibilityToBuyers ?? false
  );

  const handleSocialLinkSave = async (field: string, newValue: any) => {
    const v = value as any;
    const platformId = v?.platformId;
    const newUrl = (newValue ?? "").trim();

    if (!platformId) {
      console.error("No platformId provided for socialLink");
      return;
    }

    const currentSocialLinks = props.companyProfile?.socialLinks ?? [];
    const existingIndex = currentSocialLinks.findIndex(
      (link) => Number(link.socialPlatformId) === Number(platformId)
    );

    let updatedSocialLinks: CompanySocialLink[];

    if (newUrl === "") {
      // Remove the link if URL is empty
      updatedSocialLinks = existingIndex >= 0
        ? currentSocialLinks.filter((link) => Number(link.socialPlatformId) !== Number(platformId))
        : currentSocialLinks;
    } else if (existingIndex >= 0) {
      // Update existing link
      updatedSocialLinks = currentSocialLinks.map((link, idx) =>
        idx === existingIndex ? { ...link, url: newUrl } : link
      );
    } else {
      // Add new link
      const newLink: CompanySocialLink = {
        id: 0,
        companyProfileId: companyId,
        socialPlatformId: platformId,
        socialPlatform: platformId as SocialPlatformEnum,
        url: newUrl,
        isPrimary: false,
      };
      updatedSocialLinks = [...currentSocialLinks, newLink];
    }

    const result = await dispatch(updateSocialLinks({
      id: companyId,
      socialLinks: updatedSocialLinks
    }));

    if (updateSocialLinks.fulfilled.match(result)) {
      props.onSave(result.payload as CompanyProfile);
    }
  };

  const handleCountryMarketsSave = async (field: string, newValue: any) => {
    const arr = Array.isArray(newValue) ? newValue : [];
    const countryMarkets: CompanyCountryMarket[] = arr.map((item: any): CompanyCountryMarket => {
      if (item && typeof item === "object") {
        return {
          id: item.id ?? 0,
          companyProfileId: companyId,
          countryCode: item.countryCode ?? item.code ?? "",
          countryName: item.countryName ?? item.name ?? String(item),
        };
      }
      return {
        id: 0,
        companyProfileId: companyId,
        countryCode: "",
        countryName: String(item)
      };
    });

    const result = await dispatch(updateCountryMarkets({
      id: companyId,
      countryMarkets
    }));

    if (updateCountryMarkets.fulfilled.match(result)) {
      props.onSave(result.payload as CompanyProfile);
    }
  };

  const handleRegionMarketsSave = async (field: string, newValue: any) => {
    const arr = Array.isArray(newValue) ? newValue : [];
    const markets: CompanyMarket[] = arr.map((item: any): CompanyMarket => {
      return {
        id: item.id ?? 0,
        companyProfileId: companyId,
        region: item.region ?? item.regionId ?? item,
        regionId: item.regionId ?? item.region ?? item,
      };
    });

    const result = await dispatch(updateMarkets({
      id: companyId,
      markets
    }));

    if (updateMarkets.fulfilled.match(result)) {
      props.onSave(result.payload as CompanyProfile);
    }
  };

  const handleQuickTagsSave = async (field: string, newValue: any) => {
    const ids: number[] = (Array.isArray(newValue) ? newValue.map((x: any) => Number(x)) : []);
    const quickTags: CompanyQuickTag[] = ids.map((qid): CompanyQuickTag => ({
      id: 0,
      companyProfileId: companyId,
      quickTagId: qid,
      quickTag: qid as QuickTagEnum
    }));

    const result = await dispatch(updateQuickTags({
      id: companyId,
      quickTags
    }));

    if (updateQuickTags.fulfilled.match(result)) {
      props.onSave(result.payload as CompanyProfile);
    }
  };

  const handleCertificatesSave = async (field: string, newValue: any) => {
    const certificates: CompanyCertificates[] = Array.isArray(newValue) ? newValue : [];

    const result = await dispatch(updateCertificates({
      id: companyId,
      certificates
    }));

    if (updateCertificates.fulfilled.match(result)) {
      props.onSave(result.payload as CompanyProfile);
    }
  };

  const handleMarketplaceSave = async (field: string, newValue: any) => {
    const consents = Array.isArray(newValue) ? newValue : [];
    
    const marketplaceConsents = {
      id: 0,
      isMarketPlaceActive: marketActive,
      restrictVisibilityToBuyers: restrictVisibilityToBuyers,
      consents: consents,
    };

    const result = await dispatch(updateMarketplaceConsents({
      id: companyId,
      marketplaceConsents
    }));

    if (updateMarketplaceConsents.fulfilled.match(result)) {
      props.onSave(result.payload as CompanyProfile);
    } else {
      // Handle validation errors
      const error = result.payload as any;
      const title = error?.problem?.title;
      const detail = error?.problem?.detail;
      const errorMessage = title && detail 
        ? `${title}\n${detail}` 
        : title || detail || "Validation error occurred";
      setServerError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Handler for country code/name synchronization - both fields are updated together
  const handleCountryCodeNameSave = async (field: string, payloadValue: any, payloadText: string) => {
    const primary = await dispatch(updateCompanyProfileDescription({
      id: companyId,
      data: { fieldName: field, text: payloadText }
    }));

    if (!updateCompanyProfileDescription.fulfilled.match(primary)) {
      throw primary;
    }

    const isCode = field === "hQ_CountryCode";
    let counterpartField = isCode ? "hQ_CountryName" : "hQ_CountryCode";
    let counterpartText = "";

    if (isCode) {
      const code = String(payloadValue ?? "").trim();
      const found = countriesAtlas.find((c: any) => (c.code ?? "").toLowerCase() === code.toLowerCase());
      counterpartText = found ? (found.label ?? "") : "";
    } else {
      const name = String(payloadValue ?? "").trim();
      const found = countriesAtlas.find((c: any) => (c.label ?? "").toLowerCase() === name.toLowerCase());
      counterpartText = found ? (found.code ?? "") : "";
    }

    const secondary = await dispatch(updateCompanyProfileDescription({
      id: companyId,
      data: { fieldName: counterpartField, text: counterpartText }
    }));

    if (updateCompanyProfileDescription.fulfilled.match(secondary)) {
      props.onSave(secondary.payload as CompanyProfile);
    } else {
      props.onSave(primary.payload as CompanyProfile);
    }
  };

  const renderEditor = () => {
    const meta = FIELD_META[field as string];
    if (meta) {
      switch (meta.type) {
        case "string":
          if (field === "hQ_CountryCode" || field === "hQ_CountryName") {
            return (
              <CountrySelect
                label={field === "hQ_CountryCode" ? "Country code" : "Country"}
                displayMode={field === "hQ_CountryCode" ? "code" : "name"}
                value={String(newValue ?? "")}
                onChange={(name, option) => {
                  if (field === "hQ_CountryCode") {
                    setNewValue(option ? option.code : "");
                  } else {
                    setNewValue(option ? option.name : "");
                  }
                }}
                size="small"
              />
            );
          }

          const customLabel = field === "socialLink"
            ? (value as any)?.platformLabel ?? meta.label
            : meta.label ?? <>{field}</>;
          return (
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              size="small"
              label={customLabel}
              value={String(newValue ?? "")}
              onChange={(e) => setNewValue(e.target.value)}
            />
          );
        
        case "number":
          return (
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              size="small"
              label={meta.label ?? <>{field}</>}
              type="number"
              value={newValue == null ? "" : String(newValue)}
              onChange={(e) => {
                const v = e.target.value;
                setNewValue(v === "" ? null : Number(v));
              }}
            />
          );

        case "year":
          return (<TextField
            sx={{ mt: 1 }}
              fullWidth
              size="small"
              label={meta.label ?? <>{field}</>}
              type="number"
              value={newValue == null ? "" : String(newValue)}
              onChange={(e) => {
                const v = e.target.value;
                setNewValue(v === "" ? null : Number(v));
              }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthIcon fontSize="small" sx={{ color: 'warning.main' }} />
                </InputAdornment>
              ),
            }}
          />);

        
        case "company-size":
          return (
            <CompanyProfileCompanySizeMenu
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
          );

        case "multi-select":
          return (
            <EnumMultiSelect
              label={meta.label ? undefined : ""}
              placeholder={meta.placeholder}
              value={(Array.isArray(newValue) ? newValue : []).map((x: any) => x)}
              options={meta.options ?? []}
              onChange={(ids) => {
                setNewValue(ids ?? []);
              }}
            />
          );
        
        case "countries":
          return (
            <CountryMultiSelect
              label={meta.label ? undefined : ""}
              value={Array.isArray(newValue)
                ? newValue.map((m: any) => m?.countryName ?? m?.name ?? m)
                : []
              }
              onChange={(countries, countryOptions) => {
                setNewValue(countryOptions ?? countries);
              }}
            />
          );

        case "certificates":
          return (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6"><Localized id="companies-certificates">_Certificates</Localized></Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <CertificatesMultiSelect
                value={Array.isArray(newValue) ? newValue : []}
                onChange={(certificates) => setNewValue(certificates)}
                companyProfileId={companyId}
              />
            </Paper>
          );

        case "regions":
          return (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6"><Localized id="companies-markets-regions-label">_Markets (Regions)</Localized></Typography>
              </Box>
              <Divider sx={{ mb: 1 }} />
              <MarketRegionMultiSelect
                value={Array.isArray(newValue) ? newValue : []}
                onChange={(markets) => setNewValue(markets)}
                companyProfileId={companyId}
              />
            </Paper>
          );
        
        case "marketplace":
          return (
            <MarketplaceConsentsEditor
              marketActive={marketActive}
              restrictVisibilityToBuyers={restrictVisibilityToBuyers}
              consents={Array.isArray(newValue) ? newValue : []}
              serverError={serverError}
              onMarketActiveChange={setMarketActive}
              onRestrictVisibilityChange={setRestrictVisibilityToBuyers}
              onConsentsChange={setNewValue}
            />
          );

        default:
          return (
            <TextField
              sx={{ mt: 1 }}
              fullWidth
              multiline
              minRows={3}
              size="small"
              label={meta.label ?? <>{field}</>}
              value={typeof newValue === "string" ? newValue : JSON.stringify(newValue ?? "")}
              onChange={(e) => setNewValue(e.target.value)}
            />
          );
      }
    }

    // fallback when no meta is defined
    return (
      <TextField
        sx={{ mt: 1 }}
        fullWidth
        multiline
        minRows={3}
        size="small"
        label={<>{field}</>}
        value={typeof newValue === "string" ? newValue : JSON.stringify(newValue ?? "")}
        onChange={(e) => setNewValue(e.target.value)}
      />
    );
  };

  const handleOkClick = async () => {
    setSaving(true);
    setServerError("");

    try {
      const isCountryMarket = field === "countryMarkets";
      const isRegionMarket = field === "regionMarkets";
      const isQuickTags = field === "quickTags";
      const isCertificates = field === "certificates";
      const isSocialLink = field === "socialLink";
      const isMarketplace = field === "marketplace";
      const isCountryCodeOrName = field === "hQ_CountryCode" || field === "hQ_CountryName";

      if (isSocialLink) {
        await handleSocialLinkSave(field, newValue);
        return;
      }

      if (isCountryMarket) {
        await handleCountryMarketsSave(field, newValue);
        return;
      }

      if (isRegionMarket) {
        await handleRegionMarketsSave(field, newValue);
        return;
      }

      if (isQuickTags) {
        await handleQuickTagsSave(field, newValue);
        return;
      }

      if (isCertificates) {
        await handleCertificatesSave(field, newValue);
        return;
      }

      if (isMarketplace) {
        await handleMarketplaceSave(field, newValue);
        return;
      }

      // Normalize value for other fields
      const meta = FIELD_META[field as string];
      let payloadValue: any = newValue;

      if (meta?.type === "string") {
        payloadValue = newValue == null ? "" : String(newValue);
      } else {
        payloadValue = newValue;
      }

      // Convert to text
      let payloadText: string;
      if (payloadValue == null) {
        payloadText = "";
      } else if (typeof payloadValue === "string") {
        payloadText = payloadValue;
      } else {
        try {
          payloadText = JSON.stringify(payloadValue);
        } catch (e) {
          payloadText = String(payloadValue);
        }
      }

      if (isCountryCodeOrName) {
        await handleCountryCodeNameSave(field, payloadValue, payloadText);
        return;
      }

      const result = await dispatch(updateCompanyProfileDescription({
        id: companyId,
        data: { fieldName: field, text: payloadText }
      }));

      if (updateCompanyProfileDescription.fulfilled.match(result)) {
        props.onSave(result.payload as CompanyProfile);
      }
    } catch (e: any) {
      const msg = e?.payload?.problem?.title ?? e?.message ?? "Wystąpił błąd podczas zapisu.";
      console.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="sm">
      <DialogTitle
            sx={{
                bgcolor: (theme) => theme.palette.grey[200],
                borderBottom: "1px solid",
                borderColor: "divider",
                py: 0.75,
            }}>
        <Localized id="edit">Edycja</Localized>
      </DialogTitle>

      <DialogContent sx={{ pt: '10px !important' }}>
        {renderEditor()}
      </DialogContent>
      <DialogActions>
        <SaveButton loading={saving} onClick={handleOkClick} />
        <CancelButton loading={saving} onClick={props.onCancel} />
      </DialogActions>
    </Dialog>
  );
};
